// deployment_class: customer_env_allowed
/**
 * Developer mode — editing the template itself.
 *
 * ── The two modes (E3 2026-08-29) ──────────────────────────────────────────
 * E3: *"As a developer, I am developing the functionality. Users, if they have
 * any permissions, can just move around for customizations. They don't actually
 * change the functionality of things."*
 *
 * The line is not permission, it is KIND OF CHANGE. A developer decides what
 * exists and what it can do; a user arranges what they were given. So the edit
 * affordances in this file must never appear to a user — not disabled, not
 * greyed, absent — because a control that a user can see but not use is an
 * invitation to ask why.
 *
 * ⚠️ CORRECTED 2026-09-25. This paragraph read:
 *   *"Developer mode is gated on `internalops.read`, the scope that already
 *   draws the dev-side wall (only kathedral_dev_admin and super_admin hold it).
 *   No PHA seat can reach it…"*
 * Preserved per [[feedback_never_delete_archive_or_mark_old]]. Its last sentence
 * is now FALSE: E3 2026-09-25 — *"we DO need pha_ed (CEO) TO author - along with
 * Admins"*. The CEO is a PHA seat and reaches it.
 *
 * The gate is `write:dashboard_template`, a scope that exists for exactly this
 * capability, held by `pha_ed` and the four admin seats. It is still a scope
 * rather than a toggle anyone could flip — what changed is WHICH seats hold it,
 * not that the wall is enforced by one.
 *
 * ── What the editor does (E3's brief) ──────────────────────────────────────
 * *"I want to be able to edit and move things around. In terms of edit, I want
 * to be able to either remove it or add it or change the order of things…
 * perhaps you can add an edit icon. And when it's enabled, I can do everything.
 * And then when I click on it again, that essentially saves the template in its
 * current state. And then in the drop down… save template or save template
 * as."*
 *
 * So the toggle is the whole interaction: on = editing, off = saved. There is
 * no separate confirm step to forget, and no way to leave edit mode with work
 * hanging. Save As is the one case that needs a name, so it is the one case
 * that asks.
 *
 * ── Why removal never deletes ──────────────────────────────────────────────
 * Everything removed lands in a tray and can be put back where it was. Editing
 * a template is exploratory — you take things out to see how it reads — and an
 * irreversible removal would make that expensive. See `template-state.ts`.
 */
import { useState } from 'react';
import type { ModuleKey } from './dashboard-data';
import { type DashboardTemplate, hiddenTabsOf } from './template-state';

/**
 * The one scope that means "may author a dashboard template".
 *
 * Held by five roles (`src/v3/auth/rbac.ts`): `pha_ed`, `account_admin`,
 * `kathedral_dev_admin`, `kathedral_platform_admin`, `kathedral_super_admin`.
 * The route asks for exactly this (`routes/templates.ts` · `AUTHOR_SCOPE`), so
 * the UI gate and the server gate cannot disagree.
 */
const TEMPLATE_AUTHOR_SCOPE = 'write:dashboard_template';

/**
 * May this seat author templates — decide what EXISTS, rather than arrange what
 * they were given (`template-state.ts:28`)?
 *
 * ── The history, because the shape of it matters ───────────────────────────
 * This gated on `internalops.read` — an INTERNAL scope doing a customer's job.
 * A customer's own admin could not author a template in their own environment
 * while a Kathedral engineer could: the N40 line in the wrong place. It was
 * invisible while templates lived in `localStorage`, because nothing was ever
 * authored for anyone else.
 *
 * It briefly admitted EITHER `internalops.read` or `admin:tenant_admin`, because
 * the authoring seats sit on OPPOSITE sides of the dev-side wall (User Roles
 * spec §5/§5c) and neither scope alone covered them.
 *
 * **E3 2026-09-25 then added the CEO** — *"we DO need pha_ed (CEO) TO author -
 * along with Admins"* — and `pha_ed` holds neither admin scope. Granting it one
 * would have handed a CEO tenant-administration powers to get a single
 * capability. So the capability now has its own scope, which is what it should
 * have had from the start, and this reads ONE thing.
 *
 * The UI gate matching the server gate exactly is the point: narrower and a seat
 * cannot reach a control the server accepts; wider and the control 403s on every
 * click. A hidden button is not security, and a visible button that cannot work
 * is not a UI.
 */
export function isDeveloper(scopes: readonly string[]): boolean {
  return scopes.includes(TEMPLATE_AUTHOR_SCOPE);
}

/**
 * The edit toggle, and — while editing — the template's name and save controls.
 *
 * Rendered only for a developer. `onToggle` carries the save: turning editing
 * OFF is what commits the template, per E3's brief.
 */
export function EditBar({
  editing,
  onToggle,
  templateName,
  onSaveAs,
  dirty,
}: {
  editing: boolean;
  onToggle: () => void;
  templateName: string;
  onSaveAs: (name: string) => void;
  /** Something has changed since the last save — shown, never used to block. */
  dirty: boolean;
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState(templateName);

  return (
    <div className="kd-editbar">
      <button
        type="button"
        className={`kd-editbtn ${editing ? 'on' : ''}`}
        onClick={onToggle}
        aria-pressed={editing}
        aria-label={editing ? 'Save template and leave edit mode' : 'Edit this template'}
        title={editing ? 'Save template and stop editing' : 'Edit this template'}
      >
        <span aria-hidden="true">{editing ? '✓' : '✎'}</span>
        <span className="kd-editbtn-lb">{editing ? 'Save template' : 'Edit template'}</span>
      </button>

      {editing && (
        <>
          <span className="kd-editname">
            {templateName}
            {dirty && <span className="kd-editdirty" title="Unsaved changes"> •</span>}
          </span>

          <div className="kd-editmenu-wrap">
            <button
              type="button"
              className="kd-editmenu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="Template options"
            >
              <span aria-hidden="true">▾</span>
            </button>
            {menuOpen && (
              <div className="kd-editmenu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onToggle();
                  }}
                >
                  Save template
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setDraftName(templateName);
                    setNaming(true);
                  }}
                >
                  Save template as…
                </button>
              </div>
            )}
          </div>

          {naming && (
            <span className="kd-editas">
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                aria-label="New template name"
                placeholder="Name this template"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && draftName.trim() !== '') {
                    onSaveAs(draftName);
                    setNaming(false);
                  }
                  if (e.key === 'Escape') setNaming(false);
                }}
              />
              <button
                type="button"
                disabled={draftName.trim() === ''}
                onClick={() => {
                  onSaveAs(draftName);
                  setNaming(false);
                }}
              >
                Save as
              </button>
              <button type="button" onClick={() => setNaming(false)}>
                Cancel
              </button>
            </span>
          )}
        </>
      )}
    </div>
  );
}


/**
 * Everything currently removed, offered back.
 *
 * The tray is what makes removal safe: nothing leaves the template, it is set
 * aside. Without somewhere visible to restore from, a developer would have to
 * remember what they took out — and would stop taking things out.
 */
export function RemovedTray({
  template,
  moduleKey,
  onRestoreModule,
  onRestoreTab,
}: {
  template: DashboardTemplate;
  moduleKey: ModuleKey;
  onRestoreModule: (m: ModuleKey) => void;
  onRestoreTab: (tab: string) => void;
}): JSX.Element | null {
  const hiddenModules = template.modules.filter((m) => m.hidden);
  const hiddenTabs = hiddenTabsOf(template, moduleKey);

  // ELEMENTS ARE NO LONGER LISTED HERE (2026-08-29). The package shows every
  // element always, dimming the ones in use — so an unplaced element is already
  // visible and already draggable back in. Listing it twice would be two places
  // to look for one thing, which is the clutter E3 asked to remove.
  //
  // Modules and tabs stay because they have no palette of their own yet. When
  // they get one, this component has nothing left to do and goes away.
  if (hiddenModules.length === 0 && hiddenTabs.length === 0) {
    return null;
  }

  return (
    <div className="kd-tray" aria-label="Removed — click to put back">
      <span className="kd-tray-h">Removed</span>
      {hiddenModules.map((m) => (
        <button type="button" key={`m-${m.key}`} onClick={() => onRestoreModule(m.key)}>
          {m.key} <span aria-hidden="true">↩</span>
          <span className="sr-only"> — put back</span>
        </button>
      ))}
      {hiddenTabs.map((t) => (
        <button type="button" key={`t-${t}`} onClick={() => onRestoreTab(t)}>
          {t} <span aria-hidden="true">↩</span>
        </button>
      ))}
    </div>
  );
}
